import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import PreAuthCard from '../../shared/components/PreAuthCard';
import AdminPreAuthLayout from '../layouts/AdminPreAuthLayout';
import FormInput from '../../shared/components/FormInput';
import IconButton from '../../shared/components/IconButton';
import LinkButton from '../../shared/components/LinkButton';
import CardRadioGroup from '../../shared/components/CardRadioGroup';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { useCurrentUser } from '../auth/CurrentUserContext';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { getStoredTokens } from '../utils/tokenUtils';
import { shouldShowOnboardingForUser } from '../utils/onboardingUtils';
import { API_URL } from '../../config/env';

const TOTAL_STEPS = 3;

const ORGANIZATION_TYPE_OPTIONS = [
  {
    value: 'manufacturer',
    label: 'Manufacturer',
    description: 'Design, produce and supply equipment',
    icon: 'bi-gear-wide-connected',
  },
  {
    value: 'asset_owner',
    label: 'Asset Owner',
    description: 'Own and operate infrastructure assets',
    icon: 'bi-building',
  },
  {
    value: 'field_engineering_company',
    label: 'Field Engineering Company',
    description: 'Provide on-site engineering services',
    icon: 'bi-wrench-adjustable-circle',
  },
];

export default function RegisterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, applyTokens } = useAdminAuth();
  const { fetchCurrentUser } = useCurrentUser();

  const stepParam = searchParams.get('step');
  const initialStep = stepParam === '2' || stepParam === '3' ? Number(stepParam) : 1;
  const [step, setStep] = useState(initialStep);
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resendThrottled, setResendThrottled] = useState('');
  const [emailLoginAttempted, setEmailLoginAttempted] = useState(false);

  useEffect(() => {
    const s = searchParams.get('step');
    if (s === '2' || s === '3') setStep(Number(s));
    const e = searchParams.get('email');
    if (e) setEmail(e);
  }, [searchParams]);

  // If we arrive on step 3 with an email_login_token from the confirmation link,
  // and we're not yet authenticated, try to exchange it for OAuth tokens so the
  // user doesn't have to re-enter their password.
  useEffect(() => {
    const token = searchParams.get('email_login_token');
    if (step !== 3 || !token || isAuthenticated || emailLoginAttempted) {
      return;
    }
    setEmailLoginAttempted(true);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/v1/admin/email_login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) {
          // On any failure, just remove the token from the URL and fall back
          // to the normal login prompt.
          const next = new URLSearchParams(searchParams);
          next.delete('email_login_token');
          setSearchParams(next, { replace: true });
          return;
        }
        const data = await res.json();
        applyTokens(data.access_token, data.refresh_token);
        await fetchCurrentUser(data.access_token);
        // Clean up the token from the URL once we've successfully logged in.
        const next = new URLSearchParams(searchParams);
        next.delete('email_login_token');
        next.set('step', '3');
        setSearchParams(next, { replace: true });
      } catch {
        // Ignore network / other errors; the user can still log in manually.
      }
    })();
  }, [step, isAuthenticated, emailLoginAttempted, searchParams, setSearchParams, applyTokens, fetchCurrentUser]);

  const updateStep = (s) => {
    setStep(s);
    const next = new URLSearchParams(searchParams);
    next.set('step', String(s));
    if (email) next.set('email', email);
    setSearchParams(next, { replace: true });
  };

  // Step 1: Create account
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!agreeTerms || !agreePrivacy) {
      setError('Please agree to the Terms and Conditions and Privacy Policy.');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/v1/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            email: email.trim(),
            password,
            password_confirmation: passwordConfirmation,
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.errors?.join?.(' ') || 'Registration failed. Please try again.');
        return;
      }
      updateStep(2);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2: Resend confirmation
  const handleResend = async (e) => {
    e.preventDefault();
    setError('');
    setResendThrottled('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/v1/admin/users/confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: { email: email.trim() } }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 429) {
        setResendThrottled(data.error || 'Please wait before requesting another email.');
        return;
      }
      setError(''); // success
    } catch {
      setError('Failed to resend. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 3: Complete organization
  const handleStep3Submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!orgName.trim()) {
      setError('Please enter your organization name.');
      return;
    }
    if (!orgType) {
      setError('Please select an organization type.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/onboarding`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization: { name: orgName.trim(), organization_type: orgType },
          marketing_opt_in: marketingOptIn,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.errors?.join?.(' ') || 'Failed to save. Please try again.');
        return;
      }
      const { accessToken } = getStoredTokens();
      let currentUser = null;
      if (accessToken) {
        currentUser = await fetchCurrentUser(accessToken);
      }

      if (shouldShowOnboardingForUser(currentUser)) {
        navigate('/admin/welcome');
      } else {
        navigate('/admin');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const step1Header = {
    title: '1 Create Account',
    stepLabel: 'Step 1 of 3',
    icon: 'bi-person-plus-fill',
    variant: 'primary',
    completed: 1,
    total: TOTAL_STEPS,
  };
  const step2Header = {
    title: '2 Email Verification',
    stepLabel: 'Step 2 of 3',
    icon: 'bi-envelope-fill',
    variant: 'success',
    completed: 2,
    total: TOTAL_STEPS,
  };
  const step3Header = {
    title: '3 Organization Details',
    stepLabel: 'Step 3 of 3',
    icon: 'bi-building',
    variant: 'purple',
    completed: 3,
    total: TOTAL_STEPS,
  };

  const maxWidth = step === 3 ? 520 : 400;

  return (
    <AdminPreAuthLayout maxWidth={maxWidth}>
      {step === 1 && (
        <>
          <PreAuthCard header={step1Header}>
            <h3 className="fw-semibold mb-1">Get Started</h3>
            <p className="text-muted small mb-3">
              Create your Maintella account to access powerful field engineering tools.
            </p>
            {error && (
              <div className="alert alert-danger py-2 small" role="alert">
                {error}
              </div>
            )}
            <form onSubmit={handleStep1Submit} autoComplete="on">
              <FormInput
                id="email"
                name="email"
                type="email"
                label="Email Address *"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                icon="bi-envelope-fill"
                helperText="We'll send verification to this email."
                required
                disabled={submitting}
                autoComplete="username"
                inputClassName="py-3 rounded-3 border fw-medium"
              />
              <FormInput
                id="password"
                name="password"
                type="password"
                label="Password *"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a strong password"
                icon="bi-lock-fill"
                required
                disabled={submitting}
                autoComplete="new-password"
                inputClassName="py-3 rounded-3 border fw-medium"
              />
              <ul className="small text-muted mb-2">
                <li>At least 8 characters</li>
                <li>Contains uppercase and lowercase</li>
                <li>Contains numbers and symbols</li>
              </ul>
              <FormInput
                id="password_confirmation"
                name="password_confirmation"
                type="password"
                label="Confirm Password *"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                placeholder="Re-enter your password"
                icon="bi-lock-fill"
                required
                disabled={submitting}
                autoComplete="new-password"
                inputClassName="py-3 rounded-3 border fw-medium"
              />
              <div className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="agreeTerms"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="agreeTerms">
                  I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
                </label>
              </div>
              <div className="form-check mb-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="agreePrivacy"
                  checked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="agreePrivacy">
                  I have read and accept the <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                </label>
              </div>
              <IconButton
                type="submit"
                className="btn btn-primary w-100 py-3 fw-bold"
                icon="bi-arrow-right"
                disabled={submitting}
              >
                Continue to Verification
              </IconButton>
            </form>
            <p className="text-center mt-3 mb-0 small text-muted">
              Already have an account? <Link to="/admin/login/">Sign In</Link>
            </p>
          </PreAuthCard>
        </>
      )}

      {step === 2 && (
        <>
          <PreAuthCard header={step2Header}>
            <div className="text-center mb-3">
              <div className="border border-2 border-success rounded-3 d-inline-flex p-3 mb-2">
                <i className="bi bi-envelope-arrow-up-fill text-success fs-1" aria-hidden />
              </div>
              <h3 className="fw-semibold mb-1">Check Your Email</h3>
              <p className="text-muted small mb-0">
                We&apos;ve sent a verification link to your email address.
              </p>
            </div>
            <div className="d-flex align-items-center gap-2 rounded-3 border p-2 mb-3 bg-light">
              <i className="bi bi-envelope-fill text-muted" />
              <span className="small">Email sent to: {email}</span>
              <i className="bi bi-check-circle-fill text-success ms-auto" aria-hidden />
            </div>
            <ol className="small text-muted mb-3 ps-3">
              <li className="mb-1">
                <strong>Open your email inbox:</strong> Check your inbox for an email from Maintella.
              </li>
              <li className="mb-1">
                <strong>Click the verification link:</strong> The link will verify your email and activate your account.
              </li>
              <li>
                <strong>Complete your organization setup:</strong> You&apos;ll be redirected to complete your profile.
              </li>
            </ol>
            <div className="alert alert-warning py-2 small mb-3">
              <strong>Didn&apos;t receive the email?</strong> Check your spam folder or{' '}
              <button type="button" className="btn btn-link p-0 align-baseline" onClick={handleResend} disabled={submitting}>
                Resend verification email
              </button> 
            </div>
            {resendThrottled && <div className="alert alert-info py-2 small mb-2">{resendThrottled}</div>}
            {error && <div className="alert alert-danger py-2 small mb-2">{error}</div>}
            <IconButton
              type="button"
              className="btn btn-secondary w-100 py-2"
              icon="bi-arrow-left"
              iconPosition="start"
              onClick={() => updateStep(1)}
            >
              Back to Previous Step
            </IconButton>
            <div className="alert alert-info py-2 small mt-3 mb-0">
              <i className="bi bi-lightbulb me-1" /> Pro tip: Add app@maintella.com to your contacts to ensure you receive future notifications.
            </div>
            <p className="text-center mt-3 mb-0 small text-muted">
              Need help? <a href="/support">Contact Support</a>
            </p>
          </PreAuthCard>
        </>
      )}

      {step === 3 && (
        <>
          {!isAuthenticated ? (
            <PreAuthCard header={step3Header}>
              <h3 className="fw-semibold mb-1">Email confirmed</h3>
              <p className="text-muted small mb-3">
                Sign in to complete your organization setup.
              </p>
              <LinkButton
                to="/admin/login/"
                state={{ from: '/admin/register?step=3' }}
                variant="primary"
                icon="bi-box-arrow-in-right"
              >
                Sign in to complete setup
              </LinkButton>
            </PreAuthCard>
          ) : (
            <PreAuthCard header={step3Header}>
              <h3 className="fw-semibold mb-1">Setup Your Organization</h3>
              <p className="text-muted small mb-3">
                Tell us about your organization to customize your experience.
              </p>
              {error && (
                <div className="alert alert-danger py-2 small mb-2" role="alert">
                  {error}
                </div>
              )}
              <form onSubmit={handleStep3Submit}>
                <FormInput
                  id="orgName"
                  name="organization_name"
                  type="text"
                  label="Organization Name *"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Enter your organization name"
                  icon="bi-building"
                  helperText="This will be visible to your team members."
                  required
                  disabled={submitting}
                  inputClassName="py-3 rounded-3 border fw-medium"
                />
                <div className="mb-3">
                  <label className="form-label">Organization Type *</label>
                  <CardRadioGroup
                    name="organization_type"
                    options={ORGANIZATION_TYPE_OPTIONS}
                    selected={orgType}
                    onChange={setOrgType}
                    variant="purple"
                  />
                </div>
                <div className="alert border border-primary border-opacity-25 rounded-3 py-2 px-3 mb-3 small">
                  <i className="bi bi-info-circle me-1" />
                  <strong>What happens next?</strong>
                  <ul className="mb-0 mt-1 ps-3">
                    <li>Your workspace will be customized based on your organization type</li>
                    <li>You&apos;ll get access to relevant tools and features</li>
                    <li>Invite team members to collaborate</li>
                  </ul>
                </div>
                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="marketingOptIn"
                    checked={marketingOptIn}
                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="marketingOptIn">
                    Send me product updates, feature announcements, and engineering insights.
                  </label>
                </div>
                <div className="d-flex gap-2">
                  <IconButton
                    type="button"
                    className="btn btn-secondary py-2"
                    icon="bi-arrow-left"
                    iconPosition="start"
                    onClick={() => updateStep(2)}
                  >
                    Back
                  </IconButton>
                  <IconButton
                    type="submit"
                    className="btn flex-grow-1 py-2 fw-bold link-btn-purple"
                    icon="bi-check-lg"
                    disabled={submitting}
                  >
                    Complete Setup
                  </IconButton>
                </div>
              </form>
            </PreAuthCard>
          )}
        </>
      )}

      {step === 1 && (
        <div className="text-center mt-4 small text-muted">
          Secure • Help • Support
        </div>
      )}
    </AdminPreAuthLayout>
  );
}
